import { FC, useEffect, useState } from "react";

import { Close } from "@mui/icons-material";
import { Box, IconButton } from "@mui/material";
import { FiltersButton } from "@rubi/ui";

import { useDrawer } from "@/common/SlideoutDrawer/DrawerContext";
import SlideOutDrawer from "@/common/SlideoutDrawer/SlideoutDrawer";
import FeedbackFilterFields from "@/components/feedback/components/FeedbackFilterFields";
import FeedbackResponseDetail from "@/components/feedback/components/FeedbackResponseDetail";
import SubmissionsTable from "@/components/feedback/components/SubmissionsTable";
import SubmissionsToolbar from "@/components/feedback/components/SubmissionsToolbar";
import {
  FEEDBACK_COLLECTION_FIXTURES,
  getCollectionById,
} from "@/components/feedback/data/feedbackCollectionFixtures";
import { useFeedbackResponses } from "@/components/feedback/hooks/useFeedbackResponses";
import {
  countActiveFilters,
  EMPTY_FEEDBACK_FILTERS,
  FeedbackFilters,
  FeedbackResponseRow,
} from "@/components/feedback/types";

const DEFAULT_PAGE_SIZE = 25;

const FeedbackListPage: FC = () => {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<FeedbackFilters>(
    EMPTY_FEEDBACK_FILTERS,
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedRow, setSelectedRow] = useState<FeedbackResponseRow | null>(
    null,
  );

  const { openDrawer, closeDrawer, setDrawerWidth } = useDrawer();

  // DrawerProvider lives at the route level and outlives this page, so any
  // open state from a previous visit (open detail → switch to Dashboard →
  // return) leaves Main pushed with no drawer content. Force a closed state
  // when the list page mounts.
  useEffect(() => {
    closeDrawer();
  }, [closeDrawer]);

  const { rows, total, isLoading } = useFeedbackResponses({
    search,
    filters,
    page,
    pageSize,
  });

  const handleFiltersChange = (next: FeedbackFilters) => {
    setFilters(next);
    setPage(0);
  };

  const handleRowClick = (row: FeedbackResponseRow) => {
    setSelectedRow(row);
    setDrawerWidth(window.innerWidth * 0.5);
    openDrawer();
  };

  const handleCloseDetail = () => {
    setSelectedRow(null);
    closeDrawer();
  };

  const selectedCollection = selectedRow
    ? getCollectionById(selectedRow.feedback_collection_id)
    : undefined;

  const mainContent = (
    <Box
      sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <SubmissionsToolbar
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
        searchPlaceholder="Search"
        actions={
          <FiltersButton
            activeCount={countActiveFilters(filters)}
            onClear={() => handleFiltersChange(EMPTY_FEEDBACK_FILTERS)}
          >
            <FeedbackFilterFields
              filters={filters}
              onFiltersChange={handleFiltersChange}
              surveyOptions={FEEDBACK_COLLECTION_FIXTURES}
            />
          </FiltersButton>
        }
      />

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <SubmissionsTable
          rows={rows}
          rowCount={total}
          loading={isLoading}
          page={page}
          pageSize={pageSize}
          onPaginationChange={({ page: nextPage, pageSize: nextSize }) => {
            setPage(nextPage);
            setPageSize(nextSize);
          }}
          onRowClick={handleRowClick}
        />
      </Box>
    </Box>
  );

  const slideoutContent = selectedRow ? (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
        <IconButton
          onClick={handleCloseDetail}
          aria-label="Close feedback details"
          size="small"
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <FeedbackResponseDetail
        response={selectedRow}
        collection={selectedCollection}
      />
    </Box>
  ) : null;

  return (
    <SlideOutDrawer
      mainContent={mainContent}
      slideoutContent={slideoutContent}
      onDrawerClose={handleCloseDetail}
      subtleResize
      borderless
    />
  );
};

export default FeedbackListPage;
